import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  FileX,
  Loader2,
  XCircle,
  AlertTriangle,
  Truck,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import guidedOrderAmendmentPublicService, {
  type AmendmentData,
  type AcceptResult,
  type DeclineResult,
} from '@/services/guidedOrderAmendmentPublicService';

type ResultState =
  | { type: 'accepted'; data: AcceptResult }
  | { type: 'declined'; data: DeclineResult };

type ConfirmAction = 'accept' | 'decline' | null;

function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

function timeRemaining(isoDate: string): string {
  const diff = new Date(isoDate).getTime() - Date.now();
  if (diff <= 0) return 'Expired';
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h remaining`;
  if (hours > 0) return `${hours}h remaining`;
  const mins = Math.floor(diff / 60_000);
  return `${mins}m remaining`;
}

// ── Main Component ──────────────────────────────────────────────

const GuidedOrderAmendment = () => {
  const { token } = useParams<{ token: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['guided-order-amendment', token],
    queryFn: () => guidedOrderAmendmentPublicService.getAmendment(token!),
    enabled: !!token,
    retry: false,
  });

  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [result, setResult] = useState<ResultState | null>(null);

  // ── Handlers ────────────────────────────────────────────────

  const handleAccept = async () => {
    setProcessing(true);
    setActionError(null);
    try {
      const res = await guidedOrderAmendmentPublicService.acceptAmendment(token!);
      setResult({ type: 'accepted', data: res.data });
    } catch (err: any) {
      setActionError(err?.message || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
      setConfirmAction(null);
    }
  };

  const handleDecline = async () => {
    setProcessing(true);
    setActionError(null);
    try {
      const res = await guidedOrderAmendmentPublicService.declineAmendment(token!, declineReason || undefined);
      setResult({ type: 'declined', data: res.data });
    } catch (err: any) {
      setActionError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setProcessing(false);
      setConfirmAction(null);
    }
  };

  // ── Loading ─────────────────────────────────────────────────

  if (isLoading) {
    return (
      <PageShell>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-teal-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-neutral-900 mb-2">Verifying your link</h2>
          <p className="text-neutral-500">Please wait while we load your order details...</p>
        </motion.div>
      </PageShell>
    );
  }

  // ── Error ───────────────────────────────────────────────────

  if (error || !data?.success) {
    const msg = (error as any)?.message || 'Unknown error occurred';
    const isNotFound = /not found|expired|invalid/i.test(msg);
    const isUsed = /used|already/i.test(msg);

    return (
      <PageShell>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full">
          <Card className="border-red-200">
            <CardContent className="p-8 text-center">
              {isUsed ? (
                <>
                  <Clock className="h-14 w-14 text-orange-500 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold mb-2">Already Responded</h2>
                  <p className="text-neutral-500">This link has already been used. If you need further help, please contact your clinic.</p>
                </>
              ) : isNotFound ? (
                <>
                  <FileX className="h-14 w-14 text-red-500 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold mb-2">Link Not Found</h2>
                  <p className="text-neutral-500">This link may have expired or is invalid. Please contact your healthcare provider.</p>
                </>
              ) : (
                <>
                  <AlertCircle className="h-14 w-14 text-red-500 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold mb-2">Unable to Load</h2>
                  <p className="text-neutral-500">{msg}</p>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </PageShell>
    );
  }

  const amendment = data.data;

  // ── Result Screens ──────────────────────────────────────────

  if (result) {
    return (
      <PageShell>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full">
          <Card>
            <CardContent className="p-8 text-center">
              {result.type === 'accepted' && (
                <>
                  <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold mb-2">Payment Successful</h2>
                  <p className="text-neutral-500 mb-4">
                    An additional {formatCents(result.data.delta_charged_cents)} has been charged. Your order will now proceed to fulfillment.
                  </p>
                  <div className="bg-green-50 rounded-lg p-3 text-sm text-green-700">
                    New total: {formatCents(result.data.new_amount_cents)}
                  </div>
                </>
              )}
              {result.type === 'declined' && (
                <>
                  <XCircle className="h-16 w-16 text-neutral-400 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold mb-2">Order Cancelled</h2>
                  <p className="text-neutral-500">
                    Your order has been cancelled and a full refund of {formatCents(result.data.refund_amount_cents)} has been issued. It may take 5–10 business days to appear.
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </PageShell>
    );
  }

  // ── Review Screen (main) ────────────────────────────────────

  return (
    <PageShell>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg w-full space-y-5">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-neutral-900 mb-1">Additional Payment Required</h1>
          <p className="text-neutral-500 text-sm">
            {amendment.clinic_name && <>{amendment.clinic_name} &middot; </>}
            {amendment.program_name || 'Your treatment'}
          </p>
        </div>

        {/* Price Adjustment Card */}
        <Card>
          <CardContent className="p-6">
            <p className="text-neutral-600 text-sm mb-5">
              Your provider has finalized your prescription. The total cost is higher than your original order amount and an additional payment is required.
            </p>

            {/* Price Breakdown */}
            <div className="border rounded-lg overflow-hidden mb-5">
              <PriceRow label="Original Amount" value={formatCents(amendment.original_amount_cents)} />
              <PriceRow label="Updated Prescription Total" value={formatCents(amendment.prescription_amount_cents)} />
              <PriceRow
                label="Additional Charge"
                value={formatCents(amendment.delta_cents)}
                highlight
              />
            </div>

            {/* Deadline */}
            {amendment.auto_accept_at && (
              <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 rounded-lg px-3 py-2 mb-5">
                <Clock className="h-4 w-4 shrink-0" />
                <span>
                  Please respond by {new Date(amendment.auto_accept_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  {' '}({timeRemaining(amendment.auto_accept_at)})
                </span>
              </div>
            )}

            {/* Action Error */}
            {actionError && (
              <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-5">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{actionError}</span>
              </div>
            )}

            {/* Confirmation Dialogs */}
            {confirmAction === 'accept' && (
              <ConfirmBox
                title="Confirm Payment"
                description={`Your saved payment method will be charged ${formatCents(amendment.delta_cents)}. This payment will be processed immediately.`}
                confirmLabel="Confirm & Pay"
                confirmVariant="primary"
                loading={processing}
                onConfirm={handleAccept}
                onCancel={() => setConfirmAction(null)}
              />
            )}

            {confirmAction === 'decline' && (
              <div className="border border-red-200 bg-red-50 rounded-lg p-4 mb-5 space-y-3">
                <p className="text-sm font-medium text-red-900">Decline & Cancel Order</p>
                <p className="text-xs text-red-700">Your order will be cancelled and a full refund of {formatCents(amendment.original_amount_cents)} will be issued. This cannot be undone.</p>
                <textarea
                  className="w-full border rounded-md p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-300"
                  rows={3}
                  placeholder="Reason (optional)"
                  value={declineReason}
                  onChange={e => setDeclineReason(e.target.value)}
                  maxLength={500}
                />
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setConfirmAction(null)} disabled={processing}>
                    Back
                  </Button>
                  <Button size="sm" variant="destructive" onClick={handleDecline} disabled={processing}>
                    {processing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                    Decline & Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {!confirmAction && (
              <div className="space-y-2">
                <Button className="w-full bg-teal-600 hover:bg-teal-700" onClick={() => setConfirmAction('accept')}>
                  Accept & Pay {formatCents(amendment.delta_cents)}
                </Button>
                <Button variant="outline" className="w-full" onClick={() => setConfirmAction('decline')}>
                  Decline
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Prescription Line Items */}
        {amendment.line_items && amendment.line_items.length > 0 && (
          <Card>
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold text-neutral-900 mb-3">Order Details</h3>
              <div className="border rounded-lg overflow-hidden divide-y">
                {amendment.line_items.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-neutral-800">{item.medication}</p>
                      <p className="text-xs text-neutral-500">
                        {item.dose}
                        {item.quantity && <> &middot; Qty {item.quantity}</>}
                        {item.unit_of_measure && <> {item.unit_of_measure}</>}
                      </p>
                    </div>
                    {item.price_cents !== null && (
                      <span className="text-sm text-neutral-700">{formatCents(item.price_cents)}</span>
                    )}
                  </div>
                ))}
                {amendment.shipping && (
                  <div className="flex items-center justify-between px-4 py-3 bg-neutral-50">
                    <div className="flex items-center gap-2">
                      <Truck className="h-4 w-4 text-neutral-400" />
                      <div>
                        <p className="text-sm font-medium text-neutral-800">Shipping</p>
                        <p className="text-xs text-neutral-500">
                          {[amendment.shipping.carrier_name, amendment.shipping.service_name].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                    </div>
                    <span className="text-sm text-neutral-700">
                      {amendment.shipping.is_free_shipment
                        ? (
                          <span className="flex items-center gap-1.5">
                            {amendment.shipping.original_cost_cents > 0 && (
                              <span className="line-through text-neutral-400">{formatCents(amendment.shipping.original_cost_cents)}</span>
                            )}
                            <span className="text-green-600 font-medium">FREE</span>
                          </span>
                        )
                        : formatCents(amendment.shipping.cost_cents)}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <p className="text-xs text-neutral-400 text-center">
          If you have questions, please contact your clinic directly.
        </p>
      </motion.div>
    </PageShell>
  );
};

// ── Sub-components ────────────────────────────────────────────

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
      {children}
    </div>
  );
}

function PriceRow({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`flex items-center justify-between px-4 py-3 ${highlight ? 'bg-amber-50 font-semibold text-amber-700' : 'border-b text-neutral-700'}`}>
      <span className="text-sm">{label}</span>
      <span className={`text-sm ${highlight ? 'text-amber-700' : ''}`}>{value}</span>
    </div>
  );
}

function ConfirmBox({
  title,
  description,
  confirmLabel,
  confirmVariant,
  loading,
  onConfirm,
  onCancel,
}: {
  title: string;
  description: string;
  confirmLabel: string;
  confirmVariant: 'primary' | 'destructive';
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const isDestructive = confirmVariant === 'destructive';
  return (
    <div className={`border rounded-lg p-4 mb-5 space-y-3 ${isDestructive ? 'border-red-200 bg-red-50' : 'border-teal-200 bg-teal-50'}`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className={`h-5 w-5 shrink-0 mt-0.5 ${isDestructive ? 'text-red-500' : 'text-teal-600'}`} />
        <div>
          <p className={`text-sm font-medium ${isDestructive ? 'text-red-900' : 'text-teal-900'}`}>{title}</p>
          <p className={`text-xs mt-1 ${isDestructive ? 'text-red-700' : 'text-teal-700'}`}>{description}</p>
        </div>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onCancel} disabled={loading}>
          Back
        </Button>
        <Button
          size="sm"
          variant={isDestructive ? 'destructive' : 'default'}
          className={isDestructive ? '' : 'bg-teal-600 hover:bg-teal-700'}
          onClick={onConfirm}
          disabled={loading}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}

export default GuidedOrderAmendment;
